import { DOMAINS, type Domain } from "../../domain/contracts";
import contentVersion from "./content-version.json";

export const VISITED_ISLAND_CACHE_VERSION = "content-" + contentVersion.version;
export const VISITED_ISLAND_CACHE_MAX_ENTRIES = 180;
export const VISITED_ISLAND_CACHE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

const CACHE_NAME_PREFIX = "little-launch-island";
const URL_PARSE_BASE = "https://little-launch-island.invalid";

export interface VisitedIslandCachePolicy {
  readonly domain: Domain;
  readonly cacheName: string;
  readonly maxEntries: number;
  readonly maxAgeSeconds: number;
}

export interface VisitedIslandCachePlan {
  readonly policy: VisitedIslandCachePolicy;
  readonly resources: readonly string[];
  readonly skippedResources: readonly string[];
}

export function createVisitedIslandCachePolicy(
  domain: Domain
): VisitedIslandCachePolicy {
  return {
    domain,
    cacheName: `${CACHE_NAME_PREFIX}-${domain}-${VISITED_ISLAND_CACHE_VERSION}`,
    maxEntries: VISITED_ISLAND_CACHE_MAX_ENTRIES,
    maxAgeSeconds: VISITED_ISLAND_CACHE_MAX_AGE_SECONDS
  };
}

export function createIslandResourceUrlPattern(domain: Domain): RegExp {
  const escapedDomain = domain.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  return new RegExp(
    `\\/(?:assets\\/${escapedDomain}\\/|audio\\/(?:${escapedDomain}\\/|voice\\/${escapedDomain}\\/|music\\/${escapedDomain}(?:[./]|$)))`
  );
}

export const VISITED_ISLAND_CACHE_POLICIES: readonly VisitedIslandCachePolicy[] =
  DOMAINS.map(createVisitedIslandCachePolicy);

export function getIslandResourceDomain(resource: string | URL): Domain | null {
  const url = readUrl(resource);

  if (url === null) {
    return null;
  }

  const segments = url.pathname.split("/").filter(Boolean);
  const root = segments[0];

  if (root === "assets") {
    return readDomain(segments[1]);
  }

  if (root !== "audio") {
    return null;
  }

  if (segments[1] === "voice") {
    return readDomain(segments[2]);
  }

  if (segments[1] === "music") {
    return readMusicDomain(segments[2]);
  }

  return readDomain(segments[1]);
}

export function createVisitedIslandCachePlan(
  domain: Domain,
  resources: readonly string[],
  appOrigin: string
): VisitedIslandCachePlan {
  const policy = createVisitedIslandCachePolicy(domain);
  const baseUrl = readUrl(appOrigin);

  if (baseUrl === null) {
    return {
      policy,
      resources: [],
      skippedResources: [...resources]
    };
  }

  const selectedResources: string[] = [];
  const skippedResources: string[] = [];
  const selectedResourceSet = new Set<string>();

  for (const resource of resources) {
    const resourceUrl = readUrl(resource, baseUrl);

    if (
      resourceUrl === null ||
      resourceUrl.origin !== baseUrl.origin ||
      getIslandResourceDomain(resourceUrl) !== domain
    ) {
      skippedResources.push(resource);
      continue;
    }

    const normalizedResource = resourceUrl.pathname + resourceUrl.search;

    if (!selectedResourceSet.has(normalizedResource)) {
      selectedResourceSet.add(normalizedResource);
      selectedResources.push(normalizedResource);
    }
  }

  return {
    policy,
    resources: selectedResources,
    skippedResources
  };
}

function readDomain(value: string | undefined): Domain | null {
  if (value === undefined) {
    return null;
  }

  return DOMAINS.find((domain) => domain === value) ?? null;
}

function readMusicDomain(value: string | undefined): Domain | null {
  if (value === undefined) {
    return null;
  }

  const extensionIndex = value.lastIndexOf(".");
  const name = extensionIndex === -1 ? value : value.slice(0, extensionIndex);

  return readDomain(name);
}

function readUrl(resource: string | URL, baseUrl?: URL): URL | null {
  try {
    if (resource instanceof URL) {
      return resource;
    }

    return new URL(resource, baseUrl ?? URL_PARSE_BASE);
  } catch {
    return null;
  }
}
